/*
 * The article list.
 *
 * One row per article, which is what kb.ArticleDetail now returns: one row per
 * ArticleId, one slug, one title, one state. The state column shows the three-gate
 * verdict - can a reader see this - with a separate "draft" marker for the case the
 * verdict cannot express, a revision sitting ahead of the published one.
 *
 * Filters mirror the public ones (product, category) plus the states only
 * an administrator can see, and live in the query string so a view like "everything
 * hidden, tagged Portal" is a link.
 *
 * Its words come from the chosen language's dictionary, `t.admin.articles` (S48).
 */

import { Link, useSearchParams } from 'react-router-dom';
import { useMemo, useState } from 'react';
import type {
  AdminArticleListItem,
  AdminArticleStatusFilter,
  DeleteResult,
} from '@accotool-kb/shared';
import { useAdminApi } from '../auth/AuthProvider.js';
import { useAsync, useDebounced } from '../hooks/useAsync.js';
import {
  AudienceTags,
  DateTime,
  EmptyState,
  ErrorBanner,
  Loading,
  Pagination,
  PublishingStatusBadge,
  Tag,
} from '../components/ui.js';
import { useMessages } from '../i18n/language.js';
import { DeleteImpactDialog, type DeleteTarget } from './components/DeleteImpactDialog.js';

const PAGE_SIZE = 20;

/**
 * The saved views. `satisfies` ties the values to the shared filter list, so a
 * view this UI offers but the server cannot parse fails the build rather than the
 * request. Their labels are the dictionary's, by the same values.
 */
const STATUS_OPTIONS = [
  'all',
  'live',
  'scheduled',
  'hidden',
  'draft',
  'withdrawn',
  'unwritten',
  'untagged',
] as const satisfies ReadonlyArray<AdminArticleStatusFilter>;

function parseIds(value: string | null): number[] {
  if (!value) return [];
  return value
    .split(',')
    .map((part) => Number.parseInt(part.trim(), 10))
    .filter((id) => Number.isInteger(id) && id > 0);
}

export function ArticleListPage(): JSX.Element {
  const t = useMessages();
  const words = t.admin.articles.list;
  const api = useAdminApi();
  const [searchParams, setSearchParams] = useSearchParams();
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const metadata = useAsync(() => api.metadata(), []);

  const statusParam = searchParams.get('status');
  const status: AdminArticleStatusFilter = STATUS_OPTIONS.some(
    (option) => option === statusParam,
  )
    ? (statusParam as AdminArticleStatusFilter)
    : 'all';

  const products = parseIds(searchParams.get('products'));
  const categories = parseIds(searchParams.get('categories'));
  const page = Math.max(1, Number.parseInt(searchParams.get('page') ?? '1', 10) || 1);
  const sortParam = searchParams.get('sort');
  const sort =
    sortParam === 'created' || sortParam === 'publish' || sortParam === 'title'
      ? sortParam
      : 'modified';

  const [searchDraft, setSearchDraft] = useState(searchParams.get('q') ?? '');
  const search = useDebounced(searchDraft, 350);

  const update = (changes: Record<string, string | null>, resetPage = true) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(changes)) {
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
    }
    if (resetPage) next.delete('page');
    setSearchParams(next);
  };

  const filter = useMemo(
    () => ({
      q: search,
      products,
      categories,
      status,
      page,
      pageSize: PAGE_SIZE,
      sort: sort as 'modified' | 'created' | 'publish' | 'title',
    }),
    [search, products.join(','), categories.join(','), status, page, sort],
  );

  const state = useAsync((signal) => api.listArticles(filter, signal), [filter]);

  const onDeleted = (result: DeleteResult) => {
    setNotice(
      words.deleted(result.targetLabel, result.performed.map((step) => step.description).join(' ')),
    );
    state.reload();
  };

  return (
    <>
      <div className="page-header">
        <div>
          <h1>{t.admin.articles.shared.articles}</h1>
          <p>{state.data ? words.matching(state.data.total) : t.common.loading}</p>
        </div>
        <Link className="button button-primary" to="/admin/articles/new">
          {words.newArticle}
        </Link>
      </div>

      {notice && (
        <div className="notice notice-success" role="status">
          <div className="notice-body">{notice}</div>
          <div className="notice-actions">
            <button type="button" className="button button-quiet" onClick={() => setNotice(null)}>
              {t.common.dismiss}
            </button>
          </div>
        </div>
      )}

      <div className="toolbar">
        <div className="field toolbar-grow">
          <label htmlFor="q">{words.search}</label>
          <input
            id="q"
            type="search"
            placeholder={words.searchPlaceholder}
            value={searchDraft}
            onChange={(event) => {
              setSearchDraft(event.target.value);
              update({ q: event.target.value || null });
            }}
          />
        </div>

        <div className="field">
          <label htmlFor="status">{words.state}</label>
          <select
            id="status"
            value={status}
            onChange={(event) => update({ status: event.target.value })}
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {words.status[option]}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="product">{words.product}</label>
          <select
            id="product"
            value={products[0] ?? ''}
            onChange={(event) => update({ products: event.target.value || null })}
          >
            <option value="">{words.any}</option>
            {(metadata.data?.products ?? []).map((product) => (
              <option key={product.productId} value={product.productId}>
                {product.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="category">{words.category}</label>
          <select
            id="category"
            value={categories[0] ?? ''}
            onChange={(event) => update({ categories: event.target.value || null })}
          >
            <option value="">{words.any}</option>
            {(metadata.data?.categories ?? []).map((category) => (
              <option key={category.categoryId} value={category.categoryId}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="sort">{words.sort}</label>
          <select id="sort" value={sort} onChange={(event) => update({ sort: event.target.value })}>
            <option value="modified">{words.sortModified}</option>
            <option value="created">{words.sortCreated}</option>
            <option value="publish">{words.sortPublish}</option>
            <option value="title">{words.sortTitle}</option>
          </select>
        </div>
      </div>

      {state.error && <ErrorBanner error={state.error} onRetry={state.reload} />}
      {state.loading && !state.data && <Loading label={words.loading} />}

      {state.data && state.data.items.length === 0 && (
        <EmptyState title={words.noneMatch}>
          <p>{words.noneMatchHint}</p>
        </EmptyState>
      )}

      {state.data && state.data.items.length > 0 && (
        <>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th scope="col">{words.columnArticle}</th>
                    <th scope="col">{words.columnState}</th>
                    <th scope="col">{words.columnPublish}</th>
                    <th scope="col">{words.columnEdited}</th>
                    <th scope="col">
                      <span className="visually-hidden">{words.columnActions}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {state.data.items.map((article) => (
                    <ArticleRow
                      key={article.articleId}
                      article={article}
                      onDelete={() =>
                        setDeleteTarget({
                          kind: 'article',
                          targetId: article.articleId,
                          label: article.title ?? article.slug,
                        })
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={state.data.total}
            onChange={(nextPage) => update({ page: String(nextPage) }, false)}
          />
        </>
      )}

      <DeleteImpactDialog
        target={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={onDeleted}
      />
    </>
  );
}

function ArticleRow({
  article,
  onDelete,
}: {
  article: AdminArticleListItem;
  onDelete: () => void;
}): JSX.Element {
  const t = useMessages();
  const words = t.admin.articles.list;
  return (
    <tr>
      <td>
        <Link to={`/admin/articles/${article.articleId}`} style={{ fontWeight: 600 }}>
          {article.title ?? t.admin.articles.shared.untitled}
        </Link>
        <div className="faint">
          <code style={{ fontSize: '0.75rem' }}>{article.slug}</code>
        </div>
        <div className="tag-list" style={{ marginTop: 4 }}>
          {/*
            One product tag, never none and never two: kb.Article.ProductId is a
            NOT NULL column, so there is nothing to map over and no empty case to
            render. Only the categories can still be missing, which is all "needs
            a category" is left to say.
          */}
          <Tag kind="product">{article.product.name}</Tag>
          {article.categories.map((category) => (
            <Tag key={`c-${category.categoryId}`} kind="category">
              {category.name}
            </Tag>
          ))}
          {article.categories.length === 0 && <Tag>{words.needsCategory}</Tag>}
          {/* Who it is for, on a private article (S46) - the readers' tags, with a
              key the server no longer configures named as such. */}
          <AudienceTags
            article={{
              isPrivate: article.visibility === 'private',
              audiences: article.audiences.map((audience) => audience.name ?? words.audienceNotConfigured(audience.key)),
            }}
          />
        </div>
      </td>

      <td>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <PublishingStatusBadge status={article.status} />
          {article.hasUnpublishedDraft && (
            <span
              className="badge badge-draft"
              title={words.draftTitle(article.latestRevisionNumber, article.currentRevisionNumber)}
            >
              {t.admin.articles.shared.draft}
            </span>
          )}
        </div>
      </td>

      <td className="faint">
        <DateTime value={article.publishAtUtc} />
      </td>

      <td className="faint">
        <DateTime value={article.modifiedAtUtc} />
      </td>

      <td className="actions">
        <Link className="button button-small" to={`/admin/articles/${article.articleId}`}>
          {words.edit}
        </Link>{' '}
        <button type="button" className="button button-small button-quiet" onClick={onDelete}>
          {words.delete}
        </button>
      </td>
    </tr>
  );
}
